import { Link, useLocation } from 'react-router-dom'
import { LogOut, ClipboardList, Users, UserCircle, Building2, Settings, CreditCard } from 'lucide-react'
import { useProviderAuth } from '@/hooks/auth'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

const navItems = [
  { label: 'Visits', href: '/visits', icon: ClipboardList },
  { label: 'Patients', href: '/patients', icon: Users },
]

const settingsItems = [
  { label: 'Provider Profile', href: '/settings/provider-profile', icon: UserCircle, adminOnly: false },
  { label: 'Practice', href: '/settings/practice', icon: Building2, adminOnly: false },
  { label: 'Visit Types', href: '/settings/visit-types', icon: Settings, adminOnly: true },
  { label: 'Payments', href: '/settings/payments', icon: CreditCard, adminOnly: false },
]

export function SidebarNav() {
  const { provider, logout } = useProviderAuth()
  const location = useLocation()

  function isActive(href: string): boolean {
    if (href === '/visits') return location.pathname === '/visits' || location.pathname.startsWith('/visits/')
    return location.pathname.startsWith(href)
  }

  return (
    <nav className="flex h-full flex-col" data-testid="sidebar-nav">
      <div className="px-4 py-5">
        <Link to="/visits" className="text-lg font-display font-semibold text-primary-600">
          Determa
        </Link>
      </div>

      <Separator />

      <div className="flex-1 px-2 py-3 space-y-1">
        {navItems.map((item) => {
          const active = isActive(item.href)
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                active
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              )}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {item.label}
            </Link>
          )
        })}

        <Separator className="my-3" />

        <p className="px-3 py-1 text-xs font-medium text-slate-400 uppercase tracking-wider">Settings</p>
        {settingsItems.filter((item) => !item.adminOnly || provider?.is_admin).map((item) => {
          const active = isActive(item.href)
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                active
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              )}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {item.label}
            </Link>
          )
        })}
      </div>

      <div className="mt-auto px-2 pb-4 space-y-1">
        <Separator className="mb-3" />

        {provider && (
          <p className="px-3 py-1 text-sm font-medium text-slate-700 truncate">
            {provider.name || provider.email}
          </p>
        )}

        <Button
          variant="ghost"
          className="w-full justify-start gap-3 px-3 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          onClick={() => logout()}
        >
          <LogOut className="h-5 w-5 shrink-0" />
          Sign Out
        </Button>
      </div>
    </nav>
  )
}
